import React, { useCallback, useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Link, Logo, TopBar, QuickSearch, Button, Icon } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { LayoutResponse, TranslationsWebsite } from '@/generated/data.schemas';
import { useOnClickAway } from '@/helpers';
import { useIsNotDesktop } from '@/helpers/useIsNotDesktop';
import styles from './ClassicHeader.module.scss';
import { MegaMenu } from './MegaMenu';
import { MobileMenu } from './MobileMenu';

interface ClassicHeaderProps {
	disableQuickBasket?: boolean;
	layout: LayoutResponse | undefined;
	translations: TranslationsWebsite | undefined;
	isSticky?: boolean;
}

const ClassicHeader: React.FunctionComponent<ClassicHeaderProps> = ({ layout, translations, isSticky }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const frontPageUrl = layout?.frontPageUrl;

	const isMobile = useIsNotDesktop();
	const layoutSwitchHeader = 'md';

	const [showQuickSearch, setShowQuickSearch] = React.useState(false);
	const [scrollPos, setScrollPos] = React.useState(0);
	const [classList, setClassList] = React.useState<string[]>([]);
	const headerRef = useRef<HTMLElement>(null);
	const quickSearchRef = useRef<HTMLDivElement>(null);

	const modifyClassList = useCallback((toBeAdded: string[], toBeRemoved: string[]) => {
		setClassList((prev) => {
			const next = prev.filter((cls) => !toBeRemoved.includes(cls));
			toBeAdded.forEach((cls) => {
				if (!next.includes(cls)) next.push(cls);
			});
			return next;
		});
	}, []);

	const onScroll = useCallback(() => {
		const position = window.scrollY;
		const thresholdUp = scrollPos - 50;
		const thresholdDown = scrollPos + 150;

		if (position === 0) {
			modifyClassList([styles.absolute], [styles.push, styles.show]);
		} else if (position > 200 && position > thresholdDown && !classList.includes(styles.push)) {
			modifyClassList([styles.push], [styles.show, styles.absolute]);
		} else if (position < thresholdUp && classList.includes(styles.push)) {
			modifyClassList([styles.show], [styles.push, styles.absolute]);
		}

		if (position > thresholdDown || position < thresholdUp) {
			setScrollPos(position);
		}
	}, [scrollPos, classList, modifyClassList]);

	useEffect(() => {
		if (!isSticky) {
			return;
		}
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [onScroll, isSticky]);

	const handleCloseQuickSearch = React.useCallback((): void => {
		setShowQuickSearch(false);
	}, []);

	useOnClickAway(quickSearchRef, handleCloseQuickSearch, showQuickSearch);

	const toggleQuickSearch = (): void => {
		setShowQuickSearch(!showQuickSearch);
	};

	return (
		<header className={classNames(styles.wrapper, ...classList)} ref={headerRef}>
			<TopBar hideCustomerSelector />

			<section className={classNames(styles.header, 'u-container-lg')}>
				{isMobile && (
					<MobileMenu
						triggerButtonClassName={classNames(styles.mobileMenu, `u-hide-${layoutSwitchHeader}-min`)}
					/>
				)}

				<Link
					className={styles.logoLink}
					to={frontPageUrl || '/'}
					color={'default'}
					title={translations?.shared.siteName}
					hasMinHeight
				>
					<Logo
						className={styles.logo}
						name={segmentationId === 1 || segmentationId === 2 ?  'ccj' : 'casting'}
					/>
				</Link>

				{/* css hide class is needed because of server-side */}
				{!isMobile && <MegaMenu className={`u-hide-${layoutSwitchHeader}-max`} />}

				{isMobile ? (
					<QuickSearch className={styles.mobileSearch} />
				) : (
					<div className={styles.quickSearchWrapper}>
						{showQuickSearch ? (
							<div className={styles.quickSearchIconWrapper} ref={quickSearchRef}>
								<QuickSearch className={styles.search} autoFocusOnStart />
								<Button
									className={styles.quickSearchClose}
									variant={'ghost'}
									hasOnlyIcon
									aria-label={translations?.shared.close || 'Close'}
									onClick={toggleQuickSearch}
								>
									<Icon name={'close'} size={'md'} />
								</Button>
							</div>
						) : (
							<Button
								variant={'ghost'}
								hasOnlyIcon
								aria-label={translations?.shared.searchIconLabel || 'Search'}
								onClick={toggleQuickSearch}
							>
								<Icon name={'search'} size={'md'} />
							</Button>
						)}
					</div>
				)}
			</section>
		</header>
	);
};

export const MemoizedClassicHeader = React.memo(ClassicHeader);
